import { useState, useCallback } from 'react'
import { App } from 'antd'
import { ValidationService } from '../../features/validation'
import type { ValidationMethod } from '../../features/validation'
import type { SurveyQuestion, InterviewQuestion } from '@bandos/shared'

export type BatchValidationPhase = 'select' | 'generating' | 'review'

export type BatchValidationQuestion = SurveyQuestion | InterviewQuestion

export interface UseBatchValidationResult {
  phase: BatchValidationPhase
  questions: BatchValidationQuestion[] | null
  nodeMetadata: Record<string, { label: string; category: string }> | null
  generate: (
    mindmapId: string,
    method: ValidationMethod,
    nodeIds: string[],
    targetDurationMinutes?: number,
    language?: string
  ) => Promise<void>
  reset: () => void
}

/**
 * Orchestrates the batch validation generation lifecycle:
 * select → generating → review.
 *
 * The backend now returns a flat list of questions (each carrying
 * `sourceNodeIds`) plus a `nodeMetadata` map for display attribution. Surfaces
 * AI errors via antd notifications and resets back to selection.
 */
export function useBatchValidation(): UseBatchValidationResult {
  const { notification } = App.useApp()
  const [phase, setPhase] = useState<BatchValidationPhase>('select')
  const [questions, setQuestions] = useState<BatchValidationQuestion[] | null>(null)
  const [nodeMetadata, setNodeMetadata] = useState<Record<
    string,
    { label: string; category: string }
  > | null>(null)

  const generate = useCallback(
    async (
      mindmapId: string,
      method: ValidationMethod,
      nodeIds: string[],
      targetDurationMinutes?: number,
      language?: string
    ) => {
      setPhase('generating')
      try {
        const response = await ValidationService.generateBatchValidation(
          mindmapId,
          method,
          nodeIds,
          targetDurationMinutes,
          language
        )
        if (response.error || !response.data?.questions) {
          throw new Error(response.error?.message ?? 'No questions returned')
        }
        setQuestions(response.data.questions)
        setNodeMetadata(response.data.nodeMetadata ?? {})
        setPhase('review')
      } catch (err) {
        setPhase('select')
        notification.error({
          message: 'Failed to generate validation questions',
          description: err instanceof Error ? err.message : 'Something went wrong.',
          title: undefined,
        })
      }
    },
    [notification]
  )

  const reset = useCallback(() => {
    setPhase('select')
    setQuestions(null)
    setNodeMetadata(null)
  }, [])

  return { phase, questions, nodeMetadata, generate, reset }
}
